// ===== 4. Subcode Accordion with AI Badges =====
// src/components/SubcodeAccordion.tsx

import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
// import SmartToyIcon from '@mui/icons-material/SmartToy';
import { SubcodeRecommendation } from '../../../../services/ai/aiSubcodeRecommendationService';

interface SubcodeAccordionProps {
  subcode: {
    id: number;
    label: string;
    icon: React.ReactNode;
    content: React.ReactNode;
  };
  aiRecommendation?: SubcodeRecommendation;
  statusComponent?: React.ReactNode;
  expanded: boolean;
  panelId: string;
  onAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onSendClick: () => void;
  onHistoryClick: () => void;
}

const SubcodeAccordion: React.FC<SubcodeAccordionProps> = ({
  subcode,
  aiRecommendation,
  statusComponent,
  expanded,
  panelId,
  onAccordionChange,
  onSendClick,
  onHistoryClick
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getAIBadge = () => {
    if (!aiRecommendation) return null;

    const { required, confidence, priority } = aiRecommendation;
    const confidencePercentage = Math.round(confidence * 100);

    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <Chip
          // icon={<SmartToyIcon sx={{ fontSize: '16px !important' }} />}
          label={`Confidence Level: ${confidencePercentage}%`}
          size="small"
          color={required ? 'error' : 'warning'}
          variant="filled"
          sx={{ 
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-icon': {
              fontSize: 14
            }
          }}
        />
        {required && (
          <Chip
            label="Required"
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        )}
        {priority === 1 && (
          <Chip
            label="High Priority"
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        )}
      </Box>
    );
  };

  return (
    <Accordion
      sx={{ 
        background: aiRecommendation?.required ? '#fff5f5' : '#f6f6f6', 
        mt: 1, 
        mb: 1,
        border: aiRecommendation?.required ? `2px solid ${theme.palette.error.light}` : 'none'
      }}
      expanded={expanded}
      onChange={onAccordionChange(panelId)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${panelId}-content`}
        id={`${panelId}-header`}
        sx={{ 
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: isMobile ? 1 : 2,
            py: 1
          }
        }}
      >
        <Box display="flex" alignItems="center" flex={1} gap={isMobile ? 1 : 2}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton color="primary" size={isMobile ? "small" : "medium"}>
              {subcode.icon}
            </IconButton>
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              sx={{ 
                fontWeight: 600,
                color: aiRecommendation?.required ? theme.palette.error.main : 'inherit'
              }}
            >
              {isMobile ? subcode.label.replace(' ', '\n') : `ADD ${subcode.label}`}
            </Typography>
          </Box>

          {/* AI Badge */}
          <Box sx={{ ml: 'auto', mr: isMobile ? 1 : 2 }}>
            {getAIBadge()}
          </Box>

          {/* Status Component */}
          {statusComponent && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onHistoryClick();
              }}
              sx={{
                transition: 'transform 0.1s',
                cursor: 'pointer',
                ':hover': {
                  transform: 'scale(1.04)'
                }
              }}
            >
              {statusComponent}
            </Box>
          )}

          {/* Send Button */}
          <Box>
            <Tooltip title="Send Application Form" arrow>
              <IconButton
                color="primary"
                size={isMobile ? "small" : "medium"}
                onClick={(e) => {
                  e.stopPropagation();
                  onSendClick();
                }}
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: isMobile ? 2 : 3 }}>
        {aiRecommendation && (
          <Box mb={2} p={2} sx={{ backgroundColor: 'rgba(25, 118, 210, 0.04)', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Recommendation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {aiRecommendation.reason}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip 
                label={`Confidence: ${Math.round(aiRecommendation.confidence * 100)}%`}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`Priority: ${aiRecommendation.priority}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
        <Typography component="div">{subcode.content}</Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export default SubcodeAccordion;